import { useState } from "react";
import { Stamp, LogOut, ShieldCheck, Building2, User as UserIcon, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { assetUrl, displayUserName } from "@/lib/assetUrl";

/**
 * Sidebar: renders as a fixed left column on desktop (lg+) and as a controlled
 * drawer on mobile. Mobile open state is controlled by parent via props.
 */
export default function Sidebar({
  users,
  companies,
  currentUserId,
  onSelectUser,
  currentCompanyId,
  onSelectCompany,
  onOpenAdmin,
  mobileOpen,
  onMobileOpenChange,
}) {
  const body = (
    <SidebarBody
      users={users}
      companies={companies}
      currentUserId={currentUserId}
      onSelectUser={(id) => {
        onSelectUser(id);
        onMobileOpenChange?.(false);
      }}
      currentCompanyId={currentCompanyId}
      onSelectCompany={onSelectCompany}
      onOpenAdmin={() => {
        onOpenAdmin();
        onMobileOpenChange?.(false);
      }}
    />
  );

  return (
    <>
      <aside
        className="hidden lg:flex flex-col w-72 shrink-0 border-r border-border/60 bg-[#0F1119] min-h-screen sticky top-0 h-screen"
        data-testid="sidebar"
      >
        {body}
      </aside>

      <Sheet open={mobileOpen} onOpenChange={onMobileOpenChange}>
        <SheetContent
          side="left"
          className="w-80 max-w-[85vw] p-0 bg-[#0F1119] border-r border-border/60"
        >
          <SheetTitle className="sr-only">Meni</SheetTitle>
          {body}
        </SheetContent>
      </Sheet>
    </>
  );
}

function SidebarBody({
  users,
  companies,
  currentUserId,
  onSelectUser,
  currentCompanyId,
  onSelectCompany,
  onOpenAdmin,
}) {
  const { user: me, logout } = useAuth();
  const { t } = useT();
  const isAdmin = me?.role === "admin";
  const showAdminBtn = isAdmin || me?.role === "super_user";

  const visibleUsers =
    isAdmin && currentCompanyId
      ? users.filter((u) => u.company_id === currentCompanyId)
      : users;

  const grouped = {
    admin: visibleUsers.filter((u) => u.role === "admin"),
    super_user: visibleUsers.filter((u) => u.role === "super_user"),
    user: visibleUsers.filter((u) => u.role === "user"),
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-5 border-b border-border/60">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
            <Stamp className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.22em] text-muted-foreground font-medium leading-tight">
              {t("brand.tagline")}
            </div>
            <h1
              className="font-serif-display text-xl leading-tight tracking-tight text-white truncate"
              data-testid="sidebar-app-title"
            >
              {t("brand.title")}
            </h1>
          </div>
        </div>
      </div>

      <div className="px-5 py-4 border-b border-border/60 space-y-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium">
          {isAdmin ? t("sidebar.filterCompany") : t("sidebar.company")}
        </div>
        {isAdmin ? (
          <Select
            value={currentCompanyId || "__all__"}
            onValueChange={(v) => onSelectCompany(v === "__all__" ? null : v)}
          >
            <SelectTrigger
              className="bg-[#0C0E14] border-[#272A35] text-slate-100 font-mono-terminal text-sm h-9"
              data-testid="sidebar-company-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#131620] border-[#272A35]">
              <SelectItem value="__all__">{t("sidebar.allCompanies")}</SelectItem>
              {companies.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <div
            className="flex items-center gap-2 rounded-md border border-[#272A35] bg-[#0C0E14] px-3 py-2 text-sm text-slate-200 font-mono-terminal"
            data-testid="sidebar-company-name"
          >
            <Building2 className="h-3.5 w-3.5 text-[#D4AF37] shrink-0" />
            <span className="truncate">
              {me?.company_name || t("sidebar.noCompany")}
            </span>
          </div>
        )}

        {/* Company info card - big logo/details when a company is selected */}
        {(() => {
          const cid = isAdmin ? currentCompanyId : me?.company_id;
          const co = companies.find((c) => c.id === cid);
          if (!co) return null;
          return (
            <div
              className="rounded-lg border border-[#272A35] bg-[#0C0E14] p-3 space-y-2"
              data-testid="sidebar-company-card"
            >
              {co.logo_url ? (
                <div className="rounded-md bg-white/95 p-3 flex items-center justify-center">
                  <img
                    src={assetUrl(co.logo_url)}
                    alt={co.name}
                    className="max-h-16 w-auto object-contain"
                    data-testid="sidebar-company-logo"
                  />
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-[#272A35] py-4 flex flex-col items-center gap-1">
                  <Building2 className="h-6 w-6 text-slate-500" />
                  <span className="font-serif-display text-lg text-white text-center px-2">
                    {co.name}
                  </span>
                </div>
              )}
              <div className="text-xs text-slate-400 space-y-0.5 font-mono-terminal">
                {co.username && (
                  <div className="text-[#D4AF37]">@{co.username}</div>
                )}
                {co.address && <div className="truncate">📍 {co.address}</div>}
                {co.phone && <div>☎ {co.phone}</div>}
                {co.website && (
                  <a
                    href={co.website.startsWith("http") ? co.website : `https://${co.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#D4AF37] hover:underline truncate block"
                  >
                    {co.website}
                  </a>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium px-2 mb-2 flex items-center gap-1.5">
          <Users className="h-3 w-3" />
          {t("sidebar.users")}
          <span className="ml-auto font-mono-terminal text-slate-500">
            {visibleUsers.length}
          </span>
        </div>
        <div className="space-y-4">
          <UserGroup
            title={t("admin.role.admin")}
            items={grouped.admin}
            currentUserId={currentUserId}
            onSelect={onSelectUser}
            me={me}
            accent="text-[#F5DA6A]"
          />
          <UserGroup
            title={t("admin.role.super")}
            items={grouped.super_user}
            currentUserId={currentUserId}
            onSelect={onSelectUser}
            me={me}
            accent="text-sky-300"
          />
          <UserGroup
            title={t("admin.role.user")}
            items={grouped.user}
            currentUserId={currentUserId}
            onSelect={onSelectUser}
            me={me}
            accent="text-slate-300"
          />
        </div>
      </div>

      <div className="border-t border-border/60 p-3 space-y-2">
        <div className="rounded-md bg-[#131620] border border-[#272A35] px-3 py-2">
          <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
            {t("header.loggedInAs")}
          </div>
          <div
            className="text-sm font-mono-terminal text-slate-100 truncate mt-0.5"
            data-testid="sidebar-me"
          >
            {displayUserName(me)}
            <span className="text-slate-500"> · {me?.role}</span>
          </div>
        </div>

        {showAdminBtn && (
          <Button
            variant="secondary"
            onClick={onOpenAdmin}
            className="w-full justify-start bg-[#1A1E2C] border border-[#363A49] hover:bg-[#272A35] text-slate-100 h-9"
            data-testid="sidebar-admin-btn"
          >
            <ShieldCheck className="h-4 w-4 mr-2 text-[#D4AF37]" />
            {t("header.admin")}
          </Button>
        )}
        <Button
          variant="ghost"
          onClick={logout}
          className="w-full justify-start text-slate-300 hover:bg-red-500/10 hover:text-red-300 h-9"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="h-4 w-4 mr-2" />
          {t("header.logout")}
        </Button>
      </div>
    </div>
  );
}

function UserGroup({ title, items, currentUserId, onSelect, me, accent }) {
  if (items.length === 0) return null;
  return (
    <div>
      <div className={cn("px-2 mb-1.5 text-[10px] uppercase tracking-[0.18em] font-medium", accent)}>
        {title}
      </div>
      <ul className="space-y-0.5">
        {items.map((u) => {
          const active = u.id === currentUserId;
          const isMe = u.id === me?.id;
          return (
            <li key={u.id}>
              <button
                onClick={() => onSelect(u.id)}
                className={cn(
                  "w-full text-left rounded-md px-2 py-1.5 flex items-center gap-2 transition-colors",
                  active
                    ? "bg-[#D4AF37]/15 border border-[#D4AF37]/30"
                    : "hover:bg-[#1A1E2C] border border-transparent",
                )}
                data-testid={`sidebar-user-${u.username}`}
              >
                <UserIcon
                  className={cn(
                    "h-3.5 w-3.5 shrink-0",
                    active ? "text-[#D4AF37]" : "text-slate-500",
                  )}
                />
                <span
                  className={cn(
                    "flex-1 min-w-0 truncate font-mono-terminal text-sm",
                    active ? "text-white" : "text-slate-300",
                  )}
                >
                  {displayUserName(u)}
                </span>
                {isMe && (
                  <Badge
                    variant="outline"
                    className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[9px] h-4 px-1"
                  >
                    ja
                  </Badge>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
